import { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Alert, TextField, CircularProgress, InputAdornment, Chip,
  Stack, Link, Tooltip, Paper, Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import ViewKanbanOutlinedIcon from '@mui/icons-material/ViewKanbanOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { getProjectsBoard } from '../api/featureMap';
import {
  readProgress, PROGRESS_KIND, PROGRESS_COLOR, PRODUCT_SOURCE_LABEL, UNASSIGNED_PRODUCT,
} from '../utils/projectProgress';

// 프로젝트 관리 이전 (project-registry / 0.project-catalog) — 폴더 = 상태.
// 기존 카드 그리드를 간트 차트로 교체. 데이터는 /api/projects/board 전용 소스.
// charter 의 목표 일정(시작·완료)·책임(PL>PO>참여) 을 파싱해 타임라인 막대로 표현.
// 시작/완료가 미정인 프로젝트는 타임라인에서 제외하고 하단 "일정 미정" 섹션에 표기.
//
// FEAT-018 — 프로젝트 관리 방식이 GitHub Project 로 확정됨에 따라 막대에 진행률을
// 채우고, 차터가 가리키는 제품으로 묶어 볼 수 있게 했다. 진행 정보의 원본은 항상
// GitHub Project 이며 콘솔은 저장하지 않는다.
//
// 레이아웃: 좌측 라벨(고정 px) + 우측 타임라인(flex:1, 가용 폭 전체 사용).
// 막대·월 그리드는 % 로 배치 → 화면이 넓으면 늘어나 채우고, 월이 많아 좁아지면
// inner min-width 로 가로 스크롤. (고정 px 폭으로 좌측 쏠리던 문제 해결)

// 폴더 기반 status 메타 — catalog 전용 (기존 PROJECT_STATUS_* 와 별개).
const STATUS_META = {
  active: { label: 'active', color: '#3fb950', chip: 'success' },
  backlog: { label: 'backlog', color: '#d29922', chip: 'warning' },
  // 일시중단(P3b) — 재개 예정 상태. 기본 필터에 포함한다: 화면에서 빠지면
  //   정식 절차를 밟은 프로젝트가 '방치' 와 구분되지 않는다.
  paused: { label: 'paused', color: '#a371f7', chip: 'secondary' },
  done: { label: 'done', color: '#8b949e', chip: 'default' },
};
const STATUS_ORDER = ['active', 'backlog', 'paused', 'done'];
// 최초 진입 시 기본 필터 — done(완료) 만 제외. paused 는 보인다(재개 대상이라 시야에 둔다).
const DEFAULT_STATUSES = ['active', 'backlog', 'paused'];

const DEFAULT_LABEL_W = 248; // 좌측 라벨 컬럼 기본 폭 (px)
const MIN_LABEL_W = 140; // 라벨 컬럼 최소 폭
const MAX_LABEL_W = 620; // 라벨 컬럼 최대 폭
const LABEL_W_KEY = 'projectsGanttLabelW'; // 사용자 조절 폭 저장 키
const MIN_MONTH_W = 76; // 월 1칸 최소 폭 (px) — 이보다 좁아지면 가로 스크롤
const ROW_H = 40; // 행 높이 (px)

const DAY_MS = 86400000;
const parseISO = (s) => new Date(`${s}T00:00:00Z`);
const daysBetween = (a, b) => Math.round((parseISO(b) - parseISO(a)) / DAY_MS);
const MONTH_LABELS = ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'];

// today 를 UTC YYYY-MM-DD 로.
function todayISO() {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

// rangeStart(포함)~rangeEnd(포함) 사이의 월 목록.
// 각 월의 startOffsetDays(범위 시작으로부터의 일수) + widthDays(그 월이 차지하는 일수).
function buildMonths(rangeStartISO, rangeEndISO, totalDays) {
  const start = parseISO(rangeStartISO);
  const end = parseISO(rangeEndISO);
  const raw = [];
  let y = start.getUTCFullYear();
  let m = start.getUTCMonth();
  while (y < end.getUTCFullYear() || (y === end.getUTCFullYear() && m <= end.getUTCMonth())) {
    const firstOfMonth = `${y}-${String(m + 1).padStart(2, '0')}-01`;
    raw.push({ year: y, month: m, startOffsetDays: daysBetween(rangeStartISO, firstOfMonth) });
    m += 1;
    if (m > 11) { m = 0; y += 1; }
  }
  return raw.map((mo, i) => ({
    ...mo,
    widthDays: (i + 1 < raw.length ? raw[i + 1].startOffsetDays : totalDays) - mo.startOffsetDays,
  }));
}

const pct = (days, total) => `${(days / total) * 100}%`;

export default function ProjectsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastSynced, setLastSynced] = useState(null); // 마지막 동기화 시각 (endpoint generated_at)
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState([...DEFAULT_STATUSES]);
  // 제품 필터 — null 이면 전체. UNASSIGNED_PRODUCT 는 '제품 미지정' 만 모아 본다.
  const [selectedProduct, setSelectedProduct] = useState(null);
  // GitHub Project 조회 가능 여부. false 면 진행 정보만 비우고 목록은 그대로 그린다.
  const [progressAvailable, setProgressAvailable] = useState(true);
  const [progressError, setProgressError] = useState(null);
  // 좌측 라벨 컬럼 폭 — 드래그로 조절, localStorage 에 유지.
  const [labelW, setLabelW] = useState(() => {
    const saved = Number(localStorage.getItem(LABEL_W_KEY));
    return saved >= MIN_LABEL_W && saved <= MAX_LABEL_W ? saved : DEFAULT_LABEL_W;
  });

  // 라벨/타임라인 경계 핸들 드래그 → labelW 조절. mouseup 시 최종값 저장.
  const startResize = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = labelW;
    let finalW = startW;
    const onMove = (ev) => {
      finalW = Math.min(MAX_LABEL_W, Math.max(MIN_LABEL_W, startW + (ev.clientX - startX)));
      setLabelW(finalW);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      try { localStorage.setItem(LABEL_W_KEY, String(finalW)); } catch { /* ignore */ }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  // 더블클릭 → 기본 폭으로 리셋.
  const resetLabelW = () => {
    setLabelW(DEFAULT_LABEL_W);
    try { localStorage.setItem(LABEL_W_KEY, String(DEFAULT_LABEL_W)); } catch { /* ignore */ }
  };

  // fresh=true → 캐시 우회 강제 재-read (charter 추가/수정/삭제 즉시 반영).
  const load = async (fresh) => {
    if (fresh) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const data = await getProjectsBoard({ fresh });
      setItems(data.items || []);
      setProgressAvailable(data.progressAvailable);
      setProgressError(data.progressError);
      setLastSynced(data.generatedAt || new Date().toISOString());
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getProjectsBoard();
        if (cancelled) return;
        setItems(data.items || []);
        setProgressAvailable(data.progressAvailable);
        setProgressError(data.progressError);
        setLastSynced(data.generatedAt || new Date().toISOString());
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggleStatus = (s) =>
    setSelectedStatuses((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  // 제품 필터 옵션 — status 필터가 적용된 범위에서 등장하는 제품만 보여준다
  // (완료 프로젝트만 있는 제품이 기본 화면의 필터 칩에 남지 않도록).
  const productOptions = useMemo(() => {
    const inStatus = items.filter(
      (p) => selectedStatuses.length === 0 || selectedStatuses.includes(p.status),
    );
    const counts = new Map();
    let unassigned = 0;
    for (const p of inStatus) {
      if (!p.products?.length) { unassigned += 1; continue; }
      for (const prod of p.products) {
        const cur = counts.get(prod.id) || { id: prod.id, name: prod.name, count: 0 };
        cur.count += 1;
        counts.set(prod.id, cur);
      }
    }
    const list = [...counts.values()].sort((a, b) => b.count - a.count || a.id.localeCompare(b.id));
    if (unassigned > 0) list.push({ id: UNASSIGNED_PRODUCT, name: '제품 미지정', count: unassigned });
    return list;
  }, [items, selectedStatuses]);

  // 검색 + status + 제품 필터.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((p) => {
      if (selectedStatuses.length > 0 && !selectedStatuses.includes(p.status)) return false;
      if (selectedProduct) {
        const matched = selectedProduct === UNASSIGNED_PRODUCT
          ? !p.products?.length
          : (p.products || []).some((prod) => prod.id === selectedProduct);
        if (!matched) return false;
      }
      const haystack = `${p.title} ${p.slug} ${p.owner || ''} ${(p.products || []).map((x) => `${x.id} ${x.name}`).join(' ')}`;
      if (q && !haystack.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [items, search, selectedStatuses, selectedProduct]);

  // '이슈 미등록' 은 진행률 0% 가 아니라 관리 방식이 아직 적용되지 않은 상태다.
  // 헤더에 건수를 드러내 정착 여부가 바로 보이게 한다.
  const noIssueCount = useMemo(
    () => filtered.filter((p) => readProgress(p).kind === PROGRESS_KIND.NO_ISSUES).length,
    [filtered],
  );

  const dated = useMemo(
    () => filtered.filter((p) => p.start && p.end && p.end >= p.start),
    [filtered],
  );
  const undated = useMemo(
    () => filtered.filter((p) => !(p.start && p.end && p.end >= p.start)),
    [filtered],
  );

  const today = todayISO();

  // 타임라인 범위: 최소 시작월 ~ 최대 완료월 (+ 오늘 포함).
  const timeline = useMemo(() => {
    if (dated.length === 0) return null;
    let minStart = dated[0].start;
    let maxEnd = dated[0].end;
    for (const p of dated) {
      if (p.start < minStart) minStart = p.start;
      if (p.end > maxEnd) maxEnd = p.end;
    }
    if (today < minStart) minStart = today;
    if (today > maxEnd) maxEnd = today;
    const rangeStart = `${minStart.slice(0, 7)}-01`;
    const endD = parseISO(maxEnd);
    const lastDay = new Date(Date.UTC(endD.getUTCFullYear(), endD.getUTCMonth() + 1, 0)).getUTCDate();
    const rangeEnd = `${maxEnd.slice(0, 7)}-${String(lastDay).padStart(2, '0')}`;
    const totalDays = daysBetween(rangeStart, rangeEnd) + 1;
    const months = buildMonths(rangeStart, rangeEnd, totalDays);
    return { rangeStart, rangeEnd, totalDays, months, minTimelineW: months.length * MIN_MONTH_W };
  }, [dated, today]);

  const todayInRange = timeline && today >= timeline.rangeStart && today <= timeline.rangeEnd;

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h2">프로젝트 (Projects)</Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          {lastSynced && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              마지막 동기화: {new Date(lastSynced).toLocaleString('ko-KR', { hour12: false })}
            </Typography>
          )}
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon sx={{ animation: refreshing ? 'spin 0.8s linear infinite' : 'none', '@keyframes spin': { to: { transform: 'rotate(360deg)' } } }} />}
            onClick={() => load(true)}
            disabled={refreshing || loading}
          >
            {refreshing ? '동기화 중…' : '새로고침'}
          </Button>
        </Stack>
      </Stack>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 2 }}>
        프로젝트 차터 기반 간트 차트 — 시작·완료 일정과 담당자를 타임라인으로, 진행률은 각
        프로젝트의 GitHub Project 에서 읽어 막대에 채워 표시합니다.
        막대를 클릭하면 해당 프로젝트 차터를 엽니다.
        {' · '}총 {items.length}건 (필터: {filtered.length}건 · 타임라인 {dated.length} · 일정 미정 {undated.length}
        {noIssueCount > 0 && <> · <b style={{ color: PROGRESS_COLOR[PROGRESS_KIND.NO_ISSUES] }}>이슈 미등록 {noIssueCount}</b></>})
        <br />
        차터·GitHub Project 변경은 자동 반영됩니다. 방금 편집했다면 <b>새로고침</b>으로 즉시 최신화하세요.
      </Typography>

      {/* 진행 정보만 실패한 경우 — 차터 목록은 정상이므로 화면을 막지 않고 배너로만 알린다. */}
      {!progressAvailable && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          GitHub Project 진행 현황을 읽지 못했습니다 — 일정·담당은 정상 표시되며 진행률만 비어 있습니다.
          {progressError ? ` (${progressError})` : ''}
        </Alert>
      )}

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', md: 'center' }}
        sx={{ mb: 2 }}
      >
        <TextField
          size="small"
          placeholder="검색: 제목 / slug / 담당..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: { xs: '100%', md: 360 } }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
          }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>상태 필터:</Typography>
          {STATUS_ORDER.map((s) => {
            const active = selectedStatuses.includes(s);
            return (
              <Chip
                key={s}
                label={STATUS_META[s].label}
                size="small"
                onClick={() => toggleStatus(s)}
                variant={active ? 'filled' : 'outlined'}
                sx={{
                  cursor: 'pointer',
                  fontWeight: 600,
                  ...(active
                    ? { bgcolor: STATUS_META[s].color, color: '#0d1117', borderColor: STATUS_META[s].color }
                    : { borderColor: STATUS_META[s].color, color: STATUS_META[s].color }),
                }}
              />
            );
          })}
        </Box>
      </Stack>

      {/* 제품 필터 — 차터가 가리키는 제품 기준. '제품 미지정' 도 하나의 선택지로 둔다:
          숨기면 연결이 빠진 차터를 아무도 고치지 않는다. */}
      {productOptions.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>제품:</Typography>
          <Chip
            label="전체"
            size="small"
            onClick={() => setSelectedProduct(null)}
            variant={selectedProduct === null ? 'filled' : 'outlined'}
            color={selectedProduct === null ? 'primary' : 'default'}
            sx={{ cursor: 'pointer', fontWeight: 600 }}
          />
          {productOptions.map((opt) => {
            const active = selectedProduct === opt.id;
            const unassigned = opt.id === UNASSIGNED_PRODUCT;
            return (
              <Chip
                key={opt.id}
                label={`${opt.name} ${opt.count}`}
                size="small"
                onClick={() => setSelectedProduct(active ? null : opt.id)}
                variant={active ? 'filled' : 'outlined'}
                color={active ? 'primary' : 'default'}
                sx={{
                  cursor: 'pointer',
                  ...(unassigned && !active
                    ? { borderStyle: 'dashed', color: 'text.secondary' }
                    : null),
                }}
              />
            );
          })}
        </Box>
      )}

      {loading && <CircularProgress size={28} />}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {!loading && !error && (
        <>
          {timeline ? (
            <Paper variant="outlined" sx={{ borderRadius: 0, overflowX: 'auto', mb: 3 }}>
              {/* inner: 화면 폭 전체(100%)를 쓰되, 월이 많아 좁아지면 min-width 로 스크롤 */}
              <Box sx={{ width: '100%', minWidth: labelW + timeline.minTimelineW }}>
                {/* 월 헤더 */}
                <Box sx={{ display: 'flex', position: 'sticky', top: 0, zIndex: 3 }}>
                  <Box sx={{
                    width: labelW, flexShrink: 0, position: 'sticky', left: 0, zIndex: 4,
                    bgcolor: 'background.paper', borderBottom: '1px solid', borderRight: '1px solid',
                    borderColor: 'divider', px: 1.5, py: 1,
                  }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                      프로젝트 / 담당
                    </Typography>
                    {/* 드래그 핸들 — 라벨 컬럼 폭 조절 (더블클릭 시 기본값 복원) */}
                    <Tooltip title="드래그하여 폭 조절 · 더블클릭 시 기본값" arrow>
                      <Box
                        onMouseDown={startResize}
                        onDoubleClick={resetLabelW}
                        sx={{
                          position: 'absolute', top: 0, right: -3, width: 7, height: '100%',
                          cursor: 'col-resize', zIndex: 5,
                          '&:hover::after, &:active::after': {
                            content: '""', position: 'absolute', top: 0, right: 3, width: '2px',
                            height: '100%', bgcolor: 'primary.main',
                          },
                        }}
                      />
                    </Tooltip>
                  </Box>
                  <Box sx={{ position: 'relative', flex: 1, height: 37, bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}>
                    {timeline.months.map((mo, i) => (
                      <Box
                        key={`${mo.year}-${mo.month}`}
                        sx={{
                          position: 'absolute', left: pct(mo.startOffsetDays, timeline.totalDays),
                          width: pct(mo.widthDays, timeline.totalDays), top: 0, height: '100%',
                          borderLeft: i === 0 ? 'none' : '1px solid', borderColor: 'divider',
                          px: 0.75, py: 1, boxSizing: 'border-box',
                        }}
                      >
                        <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                          {mo.month === 0 ? <b>{mo.year}. </b> : ''}{MONTH_LABELS[mo.month]}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>

                {/* 행 */}
                {dated.map((p) => {
                  const meta = STATUS_META[p.status] || { color: '#8b949e', label: p.status };
                  const offset = daysBetween(timeline.rangeStart, p.start);
                  const spanDays = daysBetween(p.start, p.end) + 1;
                  const prog = readProgress(p);
                  const productText = p.products?.length
                    ? p.products.map((x) => x.name).join(', ')
                    : '제품 미지정';
                  return (
                    <Box key={p.slug} sx={{ display: 'flex', '&:hover .gantt-label': { bgcolor: 'action.hover' } }}>
                      <Box
                        className="gantt-label"
                        sx={{
                          width: labelW, flexShrink: 0, position: 'sticky', left: 0, zIndex: 2,
                          bgcolor: 'background.paper', borderBottom: '1px solid', borderRight: '1px solid',
                          borderColor: 'divider', px: 1.5, py: 0.5, height: ROW_H, boxSizing: 'border-box',
                          display: 'flex', flexDirection: 'column', justifyContent: 'center',
                        }}
                      >
                        <Typography variant="body2" noWrap sx={{ fontWeight: 600, lineHeight: 1.2 }} title={p.title}>
                          {p.title}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                          <Typography
                            variant="caption"
                            noWrap
                            sx={{ color: p.products?.length ? 'text.secondary' : 'warning.main', fontSize: 10, minWidth: 0 }}
                            title={`${productText} · ${p.owner || '담당 미지정'}`}
                          >
                            {productText} · {p.owner || '담당 미지정'}
                          </Typography>
                          {/* 원본으로 가는 두 통로 — 차터(왜/언제)와 GitHub Project(지금 무엇을). */}
                          {p.githubProject && (
                            <Tooltip title={`GitHub Project #${p.githubProject.number} 열기`} arrow>
                              <Link
                                href={p.githubProject.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                              >
                                <ViewKanbanOutlinedIcon sx={{ fontSize: 13 }} />
                              </Link>
                            </Tooltip>
                          )}
                          <Tooltip title="프로젝트 차터 열기" arrow>
                            <Link
                              href={p.htmlUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                            >
                              <DescriptionOutlinedIcon sx={{ fontSize: 13 }} />
                            </Link>
                          </Tooltip>
                        </Box>
                      </Box>
                      <Box sx={{ position: 'relative', flex: 1, height: ROW_H, borderBottom: '1px solid', borderColor: 'divider' }}>
                        {/* 월 그리드 라인 */}
                        {timeline.months.map((mo, i) => (
                          i === 0 ? null : (
                            <Box key={`g-${mo.year}-${mo.month}`} sx={{
                              position: 'absolute', left: pct(mo.startOffsetDays, timeline.totalDays), top: 0,
                              width: '1px', height: '100%', bgcolor: 'divider', opacity: 0.5,
                            }} />
                          )
                        ))}
                        {/* today 마커 */}
                        {todayInRange && (
                          <Box sx={{
                            position: 'absolute', left: pct(daysBetween(timeline.rangeStart, today), timeline.totalDays),
                            top: 0, width: '2px', height: '100%', bgcolor: 'error.main', opacity: 0.7, zIndex: 1,
                          }} />
                        )}
                        {/* 막대 */}
                        <Tooltip
                          arrow
                          title={
                            <Box>
                              <Typography variant="caption" sx={{ display: 'block', fontWeight: 700 }}>{p.title}</Typography>
                              <Typography variant="caption" sx={{ display: 'block' }}>상태: {meta.label}</Typography>
                              <Typography variant="caption" sx={{ display: 'block' }}>
                                제품: {productText}
                                {p.productSource ? ` (${PRODUCT_SOURCE_LABEL[p.productSource] || p.productSource})` : ''}
                              </Typography>
                              <Typography variant="caption" sx={{ display: 'block' }}>담당: {p.owner || '미지정'}</Typography>
                              <Typography variant="caption" sx={{ display: 'block' }}>일정: {p.startRaw || p.start} ~ {p.endRaw || p.end}</Typography>
                              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: PROGRESS_COLOR[prog.kind] }}>
                                진행: {prog.label}
                              </Typography>
                              <Typography variant="caption" sx={{ display: 'block' }}>{prog.detail}</Typography>
                            </Box>
                          }
                        >
                          <Box
                            component="a"
                            href={p.htmlUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              position: 'absolute',
                              left: pct(offset, timeline.totalDays),
                              width: pct(spanDays, timeline.totalDays),
                              minWidth: 8, top: '50%', transform: 'translateY(-50%)',
                              height: 18,
                              // 막대 자체는 '기간', 안쪽 채움이 '진행'. 배경을 옅게 깔고 그 위에 채운다.
                              bgcolor: `${meta.color}33`,
                              opacity: p.status === 'done' ? 0.65 : p.status === 'paused' ? 0.8 : 1,
                              border: '1px solid', borderColor: meta.color, cursor: 'pointer',
                              display: 'block', textDecoration: 'none', overflow: 'hidden',
                              '&:hover': { boxShadow: `0 0 0 2px ${meta.color}66` },
                            }}
                          >
                            {prog.kind === PROGRESS_KIND.OK ? (
                              <Box sx={{
                                width: `${prog.percent}%`, height: '100%', bgcolor: meta.color,
                                transition: 'width 200ms ease',
                              }} />
                            ) : (
                              // 진행을 모르는 상태(이슈 미등록/링크 없음/조회 실패)는 채우지 않고
                              // 빗금으로 구분한다 — 0% 로 칠하면 '시작 안 함' 과 뒤섞인다.
                              <Box sx={{
                                width: '100%', height: '100%',
                                backgroundImage: `repeating-linear-gradient(45deg, ${PROGRESS_COLOR[prog.kind]}55 0 4px, transparent 4px 8px)`,
                              }} />
                            )}
                          </Box>
                        </Tooltip>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Paper>
          ) : (
            <Alert severity="info" sx={{ mb: 3 }}>
              타임라인에 표시할 (시작·완료가 모두 지정된) 프로젝트가 없습니다.
            </Alert>
          )}

          {/* 범례 */}
          <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>범례:</Typography>
            {STATUS_ORDER.map((s) => (
              <Box key={s} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 14, height: 12, bgcolor: STATUS_META[s].color, opacity: s === 'done' ? 0.55 : s === 'paused' ? 0.7 : 0.9 }} />
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>{STATUS_META[s].label}</Typography>
              </Box>
            ))}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 2, height: 14, bgcolor: 'error.main' }} />
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>오늘</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 22, height: 12, border: '1px solid', borderColor: '#3fb950', bgcolor: '#3fb95033' }}>
                <Box sx={{ width: '60%', height: '100%', bgcolor: '#3fb950' }} />
              </Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>진행률 (GitHub Project)</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{
                width: 22, height: 12, border: '1px solid', borderColor: '#8b949e',
                backgroundImage: `repeating-linear-gradient(45deg, ${PROGRESS_COLOR[PROGRESS_KIND.NO_ISSUES]}55 0 4px, transparent 4px 8px)`,
              }} />
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>진행 정보 없음 (이슈 미등록 / 조회 실패)</Typography>
            </Box>
          </Stack>

          {/* 일정 미정 섹션 */}
          {undated.length > 0 && (
            <Box>
              <Typography variant="h4" sx={{ mb: 1 }}>
                일정 미정 <Typography component="span" variant="caption" sx={{ color: 'text.secondary' }}>({undated.length}건)</Typography>
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}>
                시작 또는 완료 일정이 확정되지 않아 타임라인에 표시하지 않은 프로젝트입니다.
              </Typography>
              <Stack spacing={1}>
                {undated.map((p) => {
                  const meta = STATUS_META[p.status] || { color: '#8b949e', label: p.status };
                  const prog = readProgress(p);
                  return (
                    <Paper key={p.slug} variant="outlined" sx={{ borderRadius: 0, px: 1.5, py: 1, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                      <Chip label={meta.label} size="small" sx={{ bgcolor: meta.color, color: '#0d1117', fontWeight: 600 }} />
                      <Box sx={{ flexGrow: 1, minWidth: 200 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.title}</Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          제품: {p.products?.length ? p.products.map((x) => x.name).join(', ') : '미지정'}
                          {' · '}담당: {p.owner || '미지정'} · 일정: {p.startRaw || 'TBD'} ~ {p.endRaw || 'TBD'}
                        </Typography>
                      </Box>
                      <Tooltip title={prog.detail} arrow>
                        <Chip
                          label={prog.label}
                          size="small"
                          variant="outlined"
                          sx={{ borderColor: PROGRESS_COLOR[prog.kind], color: PROGRESS_COLOR[prog.kind], fontWeight: 600 }}
                        />
                      </Tooltip>
                      {p.githubProject && (
                        <Link href={p.githubProject.url} target="_blank" rel="noopener noreferrer" sx={{ display: 'flex', alignItems: 'center', gap: 0.3, fontSize: 12 }}>
                          Project <OpenInNewIcon sx={{ fontSize: 13 }} />
                        </Link>
                      )}
                      <Link href={p.htmlUrl} target="_blank" rel="noopener noreferrer" sx={{ display: 'flex', alignItems: 'center', gap: 0.3, fontSize: 12 }}>
                        차터 <OpenInNewIcon sx={{ fontSize: 13 }} />
                      </Link>
                    </Paper>
                  );
                })}
              </Stack>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
